import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { formatApiError, api } from "@/lib/api";
import { CheckCircle, ArrowRight, ArrowLeft, CalendarBlank, UploadSimple } from "@phosphor-icons/react";
import { devWarn } from "@/lib/logger";

function nextBtnLabel({ step, busy }) {
  const last = step === 5;
  if (last) return busy ? "Finishing…" : "Finish";
  return busy ? "Please wait…" : "Continue";
}

const STEPS = ["Account", "Personal", "Skills", "Availability", "Documents", "Agreement"];

export default function Register() {
  const nav = useNavigate();
  const { register } = useAuth();
  const [params] = useSearchParams();
  const [role, setRole] = useState(params.get("role") === "company" ? "company" : "staff");

  if (role === "company") return <CompanyRegister nav={nav} register={register} onSwitch={() => setRole("staff")} />;
  return <StaffWizard nav={nav} register={register} onSwitch={() => setRole("company")} />;
}

function CompanyRegister({ nav, register, onSwitch }) {
  const [form, setForm] = useState({ email:"", password:"", confirm:"", first_name:"", last_name:"", phone:"", company_name:"", industry:"" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) return toast.error("Passwords don't match");
    setBusy(true);
    try {
      const u = await register({ role: "company", ...form });
      toast.success("Account created! Admin approval pending.");
      nav(`/${u.role}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail, "Registration failed")); }
    finally { setBusy(false); }
  };
  return (
    <AuthShell title="Register your company" sub="Post shifts in minutes.">
      <RoleToggle role="company" onSwitch={onSwitch} />
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Field label="First name"><Input data-testid="reg-first-name" required value={form.first_name} onChange={(e)=>setForm({...form, first_name:e.target.value})} className="h-12 mt-1.5"/></Field>
          <Field label="Last name"><Input data-testid="reg-last-name" value={form.last_name} onChange={(e)=>setForm({...form, last_name:e.target.value})} className="h-12 mt-1.5"/></Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Company"><Input data-testid="reg-company-name" required value={form.company_name} onChange={(e)=>setForm({...form, company_name:e.target.value})} className="h-12 mt-1.5"/></Field>
          <Field label="Industry"><Input data-testid="reg-industry" value={form.industry} onChange={(e)=>setForm({...form, industry:e.target.value})} className="h-12 mt-1.5"/></Field>
        </div>
        <Field label="Phone"><Input data-testid="reg-phone" value={form.phone} onChange={(e)=>setForm({...form, phone:e.target.value})} className="h-12 mt-1.5"/></Field>
        <Field label="Email"><Input data-testid="reg-email" type="email" required value={form.email} onChange={(e)=>setForm({...form, email:e.target.value})} className="h-12 mt-1.5"/></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Password"><Input data-testid="reg-password" type="password" required minLength={6} value={form.password} onChange={(e)=>setForm({...form, password:e.target.value})} className="h-12 mt-1.5"/></Field>
          <Field label="Confirm"><Input data-testid="reg-confirm" type="password" required value={form.confirm} onChange={(e)=>setForm({...form, confirm:e.target.value})} className="h-12 mt-1.5"/></Field>
        </div>
        <Button data-testid="register-submit-btn" type="submit" disabled={busy} className="w-full h-12 bg-blue-600 hover:bg-blue-700 rounded-md font-semibold">{busy ? "Creating…" : "Create account"}</Button>
      </form>
    </AuthShell>
  );
}

function StaffWizard({ nav, register, onSwitch }) {
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [account, setAccount] = useState({ email:"", password:"", confirm:"", first_name:"", last_name:"", phone:"" });
  const [personal, setPersonal] = useState({ date_of_birth:"", address:"", city:"", postcode:"" });
  const [skills, setSkills] = useState({ skills: "", experience: "", qualifications: "", preferred_locations: "", travel_distance: 25 });
  const [availDates, setAvailDates] = useState([]);
  const [docs, setDocs] = useState([]);
  const [agreed, setAgreed] = useState(false);
  const [createdUser, setCreatedUser] = useState(null);

  const canNext = () => {
    if (step === 0) return account.email && account.password && account.first_name && account.password.length >= 6 && account.password === account.confirm;
    if (step === 1) return personal.city && personal.postcode;
    if (step === 5) return agreed;
    return true;
  };

  const finish = async () => {
    if (!agreed) return toast.error("Please accept the terms.");
    setBusy(true);
    try {
      let user = createdUser;
      if (!user) {
        user = await register({ role: "staff", email: account.email, password: account.password, first_name: account.first_name, last_name: account.last_name, phone: account.phone });
        setCreatedUser(user);
      }
      // save profile
      await api.put("/staff/profile", {
        ...personal, ...skills,
        skills: skills.skills.split(",").map(s=>s.trim()).filter(Boolean),
        qualifications: skills.qualifications.split(",").map(s=>s.trim()).filter(Boolean),
        preferred_locations: skills.preferred_locations.split(",").map(s=>s.trim()).filter(Boolean),
        travel_distance: Number(skills.travel_distance),
      });
      // Geocode home postcode so live-dispatch can match without live GPS
      if (personal.postcode) {
        try { await api.post("/worker/home-location", {}); }
        catch (err) { devWarn("home-location geocode skipped", err); /* user can re-run from profile */ }
      }
      // save availability
      for (const d of availDates) {
        await api.post("/staff/availability", { date: d, start_time: "00:00", end_time: "23:59", status: "available" });
      }
      // upload docs (already uploaded on select)
      toast.success("Welcome to ShiftFlex!");
      nav(`/staff`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail, "Signup failed")); }
    finally { setBusy(false); }
  };

  const goNext = async () => {
    if (step === 0) {
      if (account.password.length < 6) return toast.error("Password must be 6+ chars");
      if (account.password !== account.confirm) return toast.error("Passwords don't match");
      setBusy(true);
      try {
        const u = await register({ role: "staff", email: account.email, password: account.password, first_name: account.first_name, last_name: account.last_name, phone: account.phone });
        setCreatedUser(u);
        setStep(1);
      } catch (err) { toast.error(formatApiError(err.response?.data?.detail, "Signup failed")); return; }
      finally { setBusy(false); }
    } else if (step === 5) {
      await finish();
    } else {
      setStep(step + 1);
    }
  };

  return (
    <AuthShell title="Create your ShiftFlex account" sub={`Step ${step+1} of ${STEPS.length} — ${STEPS[step]}`}>
      <RoleToggle role="staff" onSwitch={onSwitch} />
      <Stepper current={step} />
      <div className="mt-6 space-y-4">
        {step === 0 && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Field label="First name"><Input data-testid="reg-first-name" required value={account.first_name} onChange={(e)=>setAccount({...account, first_name: e.target.value})} className="h-12 mt-1.5"/></Field>
              <Field label="Last name"><Input data-testid="reg-last-name" value={account.last_name} onChange={(e)=>setAccount({...account, last_name: e.target.value})} className="h-12 mt-1.5"/></Field>
            </div>
            <Field label="Mobile"><Input data-testid="reg-phone" value={account.phone} onChange={(e)=>setAccount({...account, phone: e.target.value})} className="h-12 mt-1.5"/></Field>
            <Field label="Email"><Input data-testid="reg-email" type="email" required value={account.email} onChange={(e)=>setAccount({...account, email: e.target.value})} className="h-12 mt-1.5"/></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Password"><Input data-testid="reg-password" type="password" required minLength={6} value={account.password} onChange={(e)=>setAccount({...account, password: e.target.value})} className="h-12 mt-1.5"/></Field>
              <Field label="Confirm"><Input data-testid="reg-confirm" type="password" required value={account.confirm} onChange={(e)=>setAccount({...account, confirm: e.target.value})} className="h-12 mt-1.5"/></Field>
            </div>
          </>
        )}
        {step === 1 && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Date of birth"><Input data-testid="wiz-dob" type="date" value={personal.date_of_birth} onChange={(e)=>setPersonal({...personal, date_of_birth: e.target.value})} className="h-12 mt-1.5"/></Field>
              <Field label="City"><Input data-testid="wiz-city" required value={personal.city} onChange={(e)=>setPersonal({...personal, city: e.target.value})} className="h-12 mt-1.5"/></Field>
            </div>
            <Field label="Address"><Input data-testid="wiz-address" value={personal.address} onChange={(e)=>setPersonal({...personal, address: e.target.value})} className="h-12 mt-1.5"/></Field>
            <Field label="Home postcode (used to match you to nearby shifts)">
              <Input data-testid="wiz-postcode" required value={personal.postcode} onChange={(e)=>setPersonal({...personal, postcode: e.target.value})} className="h-12 mt-1.5"/>
            </Field>
            <div className="text-xs text-slate-500 -mt-2">We'll geocode this so you get live shift offers within your travel radius — even when you haven't shared live location.</div>
          </>
        )}
        {step === 2 && (
          <>
            <Field label="Skills (comma separated)"><Input data-testid="wiz-skills" value={skills.skills} onChange={(e)=>setSkills({...skills, skills: e.target.value})} placeholder="Warehousing, Forklift, First Aid" className="h-12 mt-1.5"/></Field>
            <Field label="Qualifications"><Input data-testid="wiz-quals" value={skills.qualifications} onChange={(e)=>setSkills({...skills, qualifications: e.target.value})} placeholder="Manual handling, DBS check" className="h-12 mt-1.5"/></Field>
            <Field label="Preferred locations"><Input data-testid="wiz-locs" value={skills.preferred_locations} onChange={(e)=>setSkills({...skills, preferred_locations: e.target.value})} placeholder="Manchester, Leeds" className="h-12 mt-1.5"/></Field>
            <Field label="Max travel distance (miles)"><Input data-testid="wiz-travel" type="number" value={skills.travel_distance} onChange={(e)=>setSkills({...skills, travel_distance: e.target.value})} className="h-12 mt-1.5"/></Field>
            <Field label="Experience"><Textarea data-testid="wiz-experience" rows={3} value={skills.experience} onChange={(e)=>setSkills({...skills, experience: e.target.value})} className="mt-1.5"/></Field>
          </>
        )}
        {step === 3 && (
          <div>
            <div className="text-sm text-slate-600 mb-2">Tap dates when you can work. You can update this later in your dashboard.</div>
            <Calendar mode="multiple" selected={availDates.map(d=>new Date(d))} onSelect={(dates)=>setAvailDates((dates||[]).map(d=>d.toISOString().slice(0,10)))} />
          </div>
        )}
        {step === 4 && (
          <div>
            <div className="text-sm text-slate-600 mb-3">Upload identification, right-to-work, and certifications. You can also do this later.</div>
            <UploadStack docs={docs} setDocs={setDocs} disabled={!createdUser} />
            {!createdUser && <div className="text-xs text-slate-500 mt-2">Complete step 1 first to enable uploads.</div>}
          </div>
        )}
        {step === 5 && (
          <div className="space-y-3">
            <div className="p-4 bg-slate-50 rounded-md text-sm text-slate-700 border border-slate-200 max-h-56 overflow-y-auto">
              By joining ShiftFlex you agree to abide by shift commitments you accept, maintain accurate documents and availability, and respect the platform's community rules. Payments are processed by Stripe; you'll be paid net of platform fees. Full <a className="text-blue-600" href="#">Terms</a>, <a className="text-blue-600" href="#">Privacy Policy</a> and <a className="text-blue-600" href="#">Cancellation Policy</a> apply.
            </div>
            <label className="flex items-start gap-2 cursor-pointer">
              <input data-testid="wiz-agree" type="checkbox" checked={agreed} onChange={(e)=>setAgreed(e.target.checked)} className="mt-1"/>
              <span className="text-sm text-slate-700">I've read and accept the ShiftFlex terms.</span>
            </label>
          </div>
        )}
      </div>
      <div className="mt-8 flex justify-between gap-3">
        <Button data-testid="wiz-back-btn" type="button" variant="outline" onClick={()=>setStep(Math.max(0, step-1))} disabled={step === 0}>
          <ArrowLeft size={16} className="mr-1"/> Back
        </Button>
        <Button data-testid="wiz-next-btn" type="button" onClick={goNext} disabled={busy || !canNext()} className="bg-blue-600 hover:bg-blue-700">
          {nextBtnLabel({ step, busy })} <ArrowRight size={16} className="ml-1" />
        </Button>
      </div>
    </AuthShell>
  );
}

function UploadStack({ docs, setDocs, disabled }) {
  const [type, setType] = useState("Right to Work");
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || disabled) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("document_type", type);
      const r = await api.post("/staff/documents", fd, { headers: { "Content-Type": "multipart/form-data" }});
      setDocs([...docs, r.data]);
      toast.success("Uploaded");
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed — please retry in a moment."); }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <select value={type} onChange={(e)=>setType(e.target.value)} className="h-11 border border-slate-300 rounded-md px-3 text-sm bg-white">
          {["Right to Work","Identification","Certificate","Qualification","Training"].map(t=>(<option key={t}>{t}</option>))}
        </select>
        <label className={`h-11 px-4 rounded-md text-sm font-semibold inline-flex items-center gap-2 ${disabled ? "bg-slate-200 text-slate-500 cursor-not-allowed" : "bg-blue-600 text-white cursor-pointer hover:bg-blue-700"}`}>
          <UploadSimple size={16}/> {busy ? "Uploading…" : "Add document"}
          <input type="file" onChange={onFile} disabled={disabled} className="hidden" data-testid="wiz-doc-input"/>
        </label>
      </div>
      <div className="space-y-1.5">
        {docs.map((d) => (<div key={d.id} className="p-2.5 border border-slate-200 rounded-md text-sm flex items-center justify-between"><span>{d.document_type} — {d.original_filename}</span><CheckCircle size={16} className="text-emerald-500" weight="fill"/></div>))}
      </div>
    </div>
  );
}

function AuthShell({ title, sub, children }) {
  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-lg">
        <Link to="/" className="font-display text-2xl font-extrabold text-slate-900 block mb-8">Shift<span className="text-blue-600">Flex</span></Link>
        <div className="bg-white border border-slate-200 rounded-md p-8">
          <h1 className="font-display text-3xl font-extrabold text-slate-900 mb-1">{title}</h1>
          <p className="text-slate-600 mb-6">{sub}</p>
          {children}
          <div className="mt-6 text-sm text-slate-600">
            Already have an account? <Link to="/login" data-testid="link-to-login" className="text-blue-600 font-semibold">Log in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function RoleToggle({ role, onSwitch }) {
  return (
    <div className="grid grid-cols-2 gap-2 p-1 bg-slate-100 rounded-md mb-6">
      <button type="button" data-testid="role-staff-btn" onClick={role === "staff" ? undefined : onSwitch}
        className={`py-2.5 rounded-md text-sm font-semibold transition-colors ${role==="staff" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600"}`}>I'm looking for work</button>
      <button type="button" data-testid="role-company-btn" onClick={role === "company" ? undefined : onSwitch}
        className={`py-2.5 rounded-md text-sm font-semibold transition-colors ${role==="company" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600"}`}>I'm hiring staff</button>
    </div>
  );
}

function Stepper({ current }) {
  return (
    <div className="flex items-center justify-between gap-1">
      {STEPS.map((s, i) => (
        <div key={s} className="flex-1 flex flex-col items-center">
          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${i <= current ? "bg-blue-600 text-white" : "bg-slate-200 text-slate-600"}`}>
            {i < current ? "✓" : i+1}
          </div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-1 hidden sm:block">{s}</div>
        </div>
      ))}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="label-caps">{label}</Label>
      {children}
    </div>
  );
}
